/**
 * Robot tasks as the redesign shows them (docs/redesign-2026-11/design/pages.html, W01–W05): clean titles, the
 * kitchen area, the task's own time and points (hidden when unknown, never a default), the Next up reason, and the
 * corner chip from the person's own runs. Pure helpers; pages and tests share them.
 */
import { named, statusOf, type Mission, type SimRunRow, type Status } from '@robotensor/api'

/** A run's status in the shared set (the list's `shared_status` when the service sends it, else its raw status). */
export function runStatus(r: SimRunRow): Status | null {
  return statusOf({ ...r, status: r.shared_status ?? r.status })
}

/** "Robot control: Open the left drawer" → "Open the left drawer". */
export function cleanTitle(title: string | null | undefined): string {
  return (title ?? '').replace(/^\s*robot control\s*[:·-]\s*/i, '').trim() || 'Robot task'
}

/** "~1 min" from the task's own seconds; null when the task doesn't say. */
export function estLabel(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  return `~${Math.max(1, Math.round(seconds / 60))} min`
}

export function pointsLabel(points: number | null | undefined): string | null {
  return typeof points === 'number' && Number.isFinite(points) && points > 0 ? `${points} pts` : null
}

/** "Drawers · ~1 min · 10 pts", leaving out whatever the task doesn't know. */
export function taskMeta(m: Pick<Mission, 'area' | 'est_seconds' | 'points'>, opts: { area?: boolean } = {}): string {
  return [opts.area === false ? null : (m.area ?? null), estLabel(m.est_seconds), pointsLabel(m.points)].filter(Boolean).join(' · ')
}

export type WhyKey = 'continue' | 'new' | 'bookmarked' | 'needed'

export const WHY_LABEL: Record<WhyKey, string> = {
  continue: 'Continue',
  new: 'New for you',
  bookmarked: 'Bookmarked',
  needed: 'Most needed',
}

/** The Next up reason in words: "New for you", or a new reason's key in words. */
export function whyLabel(m: Pick<Mission, 'next_up_reason' | 'assignment' | 'bookmarked' | 'highlights'>): string {
  return named(WHY_LABEL, whyOf(m))
}

/** Why a task is in Next up: the feed says so (`next_up_reason`); older feeds are read from what they carry. A reason
 * this build doesn't know comes back as sent (`whyLabel` puts it in words). */
export function whyOf(m: Pick<Mission, 'next_up_reason' | 'assignment' | 'bookmarked' | 'highlights'>): WhyKey | (string & {}) {
  if (m.next_up_reason) return m.next_up_reason
  if (m.assignment && m.assignment.status === 'active') return 'continue'
  if (m.bookmarked) return 'bookmarked'
  if ((m.highlights ?? []).includes('new_for_you')) return 'new'
  return 'needed'
}

export type CornerChip = { label: string; tone: 'ok' | 'new' | 'prac' } | null

/** Done ✓ once a run of it was accepted, Practice when only practice runs passed, New when the feed says so. */
export function cornerChip(m: Pick<Mission, 'id' | 'next_up_reason' | 'highlights'>, runs: readonly SimRunRow[] | undefined): CornerChip {
  const mine = (runs ?? []).filter((r) => r.mission_id === m.id)
  const passed = mine.map((r) => runStatus(r)?.key)
  if (passed.includes('accepted')) return { label: 'Done ✓', tone: 'ok' }
  if (passed.includes('practice')) return { label: 'Practice', tone: 'prac' }
  if (m.next_up_reason === 'new' || (m.highlights ?? []).includes('new_for_you')) return { label: 'New', tone: 'new' }
  return null
}

/** Not done: no accepted run of it yet. */
export function isDone(m: Pick<Mission, 'id'>, runs: readonly SimRunRow[] | undefined): boolean {
  return (runs ?? []).some((r) => r.mission_id === m.id && runStatus(r)?.key === 'accepted')
}

/** The quick filters on Robot › Tasks. */
export const QUICK = ['notdone', 'bookmarked', 'short'] as const
export type Quick = (typeof QUICK)[number]
export const QUICK_LABEL: Record<Quick, string> = { notdone: 'Not done', bookmarked: 'Bookmarked', short: 'Under 2 min' }

export function quickMatch(m: Mission, f: Quick | undefined, runs: readonly SimRunRow[] | undefined): boolean {
  if (!f) return true
  if (f === 'bookmarked') return m.bookmarked === true
  if (f === 'short') return typeof m.est_seconds === 'number' && m.est_seconds > 0 && m.est_seconds < 120
  return !isDone(m, runs)
}

/** "0:48" from seconds. */
export function clock(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '–'
  const s = Math.round(seconds)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** "Thursday 12 Nov". */
export function longDay(d: Date): string {
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })
}

/** "Today 10:42", "Yesterday", "Mon 26 Oct". */
export function when(iso: string | null | undefined, withTime = true): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const days = Math.round((new Date(new Date().toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000)
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (days === 0) return withTime ? `Today ${time}` : 'Today'
  if (days === 1) return 'Yesterday'
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** "10:42" today, "yesterday", else "Mon 26 Oct" (lists where the kind of work comes first). */
export function shortWhen(iso: string | null | undefined): string {
  const w = when(iso)
  if (w.startsWith('Today ')) return w.slice(6)
  return w === 'Yesterday' ? 'yesterday' : w
}

/** The sim submission id inside a contribution's ref (`sim:submission:<id>`). */
export function submissionOfRef(ref: string | null | undefined): string | null {
  const m = /^sim:submission:(.+)$/.exec(ref ?? '')
  return m ? m[1]! : null
}

/** A realdata capture id inside a contribution's ref, when it names one. */
export function captureOfRef(ref: string | null | undefined): string | null {
  const m = /^realdata:(?:capture|episode_set|submission):([^:@]+)/.exec(ref ?? '')
  return m ? m[1]! : null
}
